// Compliance candidate status vocabulary (mirrors the backend
// CandidateComplianceStatus enum). Used by the list filter + status badges.

export const COMPLIANCE_STATUS = {
  PENDING_FROM_CANDIDATE: "pending_from_candidate",
  PENDING_FOR_REVIEW: "pending_for_review",
  RE_REQUESTED: "re_requested",
  APPROVED: "approved",
  REJECTED: "rejected",
};

// Why a compliance run was released (mirrors the backend ReleaseReason enum in
// app/db/models.py — the two are spelled out twice, which is a debt, not a
// pattern to copy; there is no constants channel between the two languages).
//
// Deliberately NOT part of COMPLIANCE_STATUS above: releasing a run says
// nothing about where the documents got to, and a release must never render as
// a rejection.
export const RELEASE_REASON = {
  TRANSFERRED_TO_ANOTHER_JOB: "transferred_to_another_job",
  CANDIDATE_WITHDREW: "candidate_withdrew",
  ROLE_CLOSED_OR_ON_HOLD: "role_closed_or_on_hold",
  DUPLICATE_RUN: "duplicate_run",
  OTHER: "other",
};

// The labels the backend's RELEASE_REASON_LABELS uses for the audit timeline.
// Keep the two in step: the dropdown and the timeline entry for one release
// should read the same words.
export const releaseReasonOptions = [
  { value: RELEASE_REASON.TRANSFERRED_TO_ANOTHER_JOB, label: "Transferred to another job" },
  { value: RELEASE_REASON.CANDIDATE_WITHDREW, label: "Candidate withdrew" },
  { value: RELEASE_REASON.ROLE_CLOSED_OR_ON_HOLD, label: "Role closed / on hold" },
  { value: RELEASE_REASON.DUPLICATE_RUN, label: "Duplicate run" },
  { value: RELEASE_REASON.OTHER, label: "Other" },
];

// The server's cap on the free-text note (max_length=2000). Mirrored so the
// modal refuses what the route refuses, rather than letting someone type 2500
// characters and collecting a 422.
export const RELEASE_NOTE_MAX_LENGTH = 2000;

export const complianceStatusFilterOption = [
  { value: COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE, label: "Pending from Candidate" },
  { value: COMPLIANCE_STATUS.PENDING_FOR_REVIEW, label: "Pending for Review" },
  { value: COMPLIANCE_STATUS.RE_REQUESTED, label: "Re-requested" },
  { value: COMPLIANCE_STATUS.APPROVED, label: "Approved" },
  { value: COMPLIANCE_STATUS.REJECTED, label: "Rejected" },
];

// Human label + Badge colorIndex (see components/ui/Badge.jsx palette) per status.
export const COMPLIANCE_STATUS_META = {
  [COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE]: { label: "Pending from Candidate", colorIndex: 7 },
  [COMPLIANCE_STATUS.PENDING_FOR_REVIEW]: { label: "Pending for Review", colorIndex: 0 },
  [COMPLIANCE_STATUS.RE_REQUESTED]: { label: "Re-requested", colorIndex: 5 },
  [COMPLIANCE_STATUS.APPROVED]: { label: "Approved", colorIndex: 3 },
  [COMPLIANCE_STATUS.REJECTED]: { label: "Rejected", colorIndex: 6 },
};

export const getComplianceStatusMeta = (status) => COMPLIANCE_STATUS_META[status] ?? { label: status ?? "—", colorIndex: 8 };

/** Index live compliance runs by the application that owns them (Phase G).
 *
 * A run belongs to exactly one application (`owning_application_id`), and a
 * candidate-list row **is** an application — so this is the only key that
 * answers "is THIS row in compliance".
 *
 * It replaces an index built from candidate id, lowercased email and
 * lowercased display name. That one gave every row of one person the same
 * answer, so a candidate approved for one job showed as approved on the job
 * nobody had sent them to; and it gave two different people who happen to
 * share a name a single compliance status between them.
 *
 * A run with no owner is deliberately dropped rather than indexed under its
 * candidate. Those are the runs created before the owner was recorded, and
 * attributing one to every row of that candidate is precisely the bug.
 */
export const indexComplianceRunsByApplication = (runs) =>
  Object.fromEntries((runs ?? []).filter((run) => run?.owningApplicationId).map((run) => [String(run.owningApplicationId), run.status]));

/** The compliance status of one list row, or undefined.
 *
 * Undefined means "this application has not been sent", which is what the
 * Send button keys off. A row carrying no application id cannot be matched at
 * all — falling back to the candidate is what this function exists to stop.
 */
export const complianceStatusForRow = (indexByApplication, row) => {
  const applicationId = row?.application_id;
  if (!applicationId) return undefined;
  return indexByApplication?.[String(applicationId)];
};

// REMOVED: COMPLIANCE_TRIGGER_STAGE_OPTIONS.
//
// A hardcoded four-member list of the stages compliance could start at. Phase 6
// stopped the settings dropdown reading it — but left the export in place, so
// this note sat directly above the constant it said had been removed, and the
// candidate list went on importing it. Phase S2 finished the job: compliance
// starts from anywhere in the pipeline now, so there is no stage to list.
//
// There is deliberately no constant to replace this one.

// Field types a required-field can take (mirrors backend ALLOWED_REQUIRED_FIELD_TYPES).
export const REQUIRED_FIELD_TYPES = ["text", "number", "date", "boolean", "select", "email", "phone"];

// SLA queue thresholds, in days since the candidate was sent to compliance.
// A candidate crosses into "warning" then "overdue"; an approved candidate is
// settled and never flagged.
//
// These drive the aging badge under the Sent Date column only. There is no SLA
// *filter*: "show me candidates who are 2-3 days old" is not a question anyone
// asks — the badge already draws the eye to the late ones while the officer
// reads the list they actually filtered for.
export const SLA_WARNING_DAYS = 2;
export const SLA_OVERDUE_DAYS = 3;

// Upload attestation (Phase 8C).
//
// ⚠️ PLACEHOLDER COPY, pending sign-off with higher authorities. Deliberately a
// single constant so the change is one line — the backend stores the wording
// that was actually displayed on each consent row, so editing this does not
// rewrite what past uploaders agreed to.
//
// The backend has its own copy (schema/api.py) and that is the one persisted;
// this is what the officer reads before ticking the box. Keep the two in step.
export const COMPLIANCE_OFFICER_UPLOAD_ATTESTATION =
  "The officer confirms they are authorized to upload this document on the " +
  "candidate's behalf for compliance verification purposes, that it was " +
  "provided by the candidate and is being uploaded unaltered, and that the " +
  "candidate has been informed of and consents to this submission. It is " +
  "acknowledged that AI or automated tools may be used as part of the " +
  "compliance review process, with all final decisions subject to human " +
  "review. The officer represents that this submission is authorized under " +
  "applicable data protection laws, and accepts accountability for this " +
  "submission.";
